import apiClient, { getAuthHeaders } from './apiClient';
import { API_BASE_URL } from '../constants/config';
import type { Photo, UploadUrlResponse, AlphaAngle, BatchUploadResult, BatchProgressEvent } from '../types/photo';

export async function getPhotos(wodKey: string): Promise<Photo[]> {
  const res = await apiClient.get<Photo[]>(`/workorderdetails/${wodKey}/photos`);
  return res.data;
}

export async function requestUploadUrl(
  wodKey: string,
  file: { FileName: string; FileSize: number; MimeType: string; Stage: string; Position: string },
): Promise<UploadUrlResponse> {
  const res = await apiClient.post<UploadUrlResponse>(
    `/workorderdetails/${wodKey}/photos/upload-url`,
    file,
  );
  return res.data;
}

export function uploadToBlob(
  uploadUrl: string,
  file: File,
  onProgress?: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl, true);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed with status ${xhr.status}`));
    };

    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.send(file);
  });
}

export async function confirmUpload(photoKey: string): Promise<Photo> {
  const res = await apiClient.post<Photo>(`/workorderdetails/photos/${photoKey}/confirm`);
  return res.data;
}

export async function requestRecropUrl(photoKey: string): Promise<{ uploadUrl: string }> {
  const res = await apiClient.post<{ uploadUrl: string }>(
    `/workorderdetails/photos/${photoKey}/recrop-url`,
  );
  return res.data;
}

export async function confirmRecrop(photoKey: string): Promise<Photo> {
  const res = await apiClient.post<Photo>(`/workorderdetails/photos/${photoKey}/confirm-recrop`);
  return res.data;
}

export async function updatePhoto(photoKey: string, data: { Comment: string | null }): Promise<Photo> {
  const res = await apiClient.put<Photo>(`/workorderdetails/photos/${photoKey}`, data);
  return res.data;
}

export async function deletePhoto(photoKey: string): Promise<void> {
  await apiClient.delete(`/workorderdetails/photos/${photoKey}`);
}

export async function downloadZip(wodKey: string, photoKeys?: string[]): Promise<void> {
  const res = await apiClient.post(
    `/workorderdetails/${wodKey}/photos/download-zip`,
    { photoKeys },
    { responseType: 'blob' },
  );
  const blob = new Blob([res.data], { type: 'application/zip' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'photos.zip';
  a.click();
  window.URL.revokeObjectURL(url);
}

export async function batchUpload(
  wodKey: string,
  files: File[],
  alphaAngle: AlphaAngle,
  onProgress?: (evt: BatchProgressEvent) => void,
): Promise<BatchUploadResult[]> {
  const formData = new FormData();
  files.forEach((f) => formData.append('files', f));
  formData.append('alphaAngle', alphaAngle);

  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/workorderdetails/${wodKey}/photos/batch-upload`, {
    method: 'POST',
    headers: { ...authHeaders },
    body: formData,
  });

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `Upload failed (${response.status})`);
  }

  // Parse SSE stream
  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response stream');

  const decoder = new TextDecoder();
  let buffer = '';
  let results: BatchUploadResult[] = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Parse SSE lines: "data: {...}\n\n"
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const dataLine = part.trim().replace(/^data:\s*/, '');
      if (!dataLine) continue;
      try {
        const evt: BatchProgressEvent = JSON.parse(dataLine);
        onProgress?.(evt);
        if (evt.type === 'complete' && evt.results) {
          results = evt.results;
        }
      } catch { /* skip malformed */ }
    }
  }
  return results;
}

export async function slotUpload(
  wodKey: string, file: File, stage: string, position: string,
  alphaAngle: AlphaAngle,
  onProgress?: (evt: BatchProgressEvent) => void,
): Promise<BatchUploadResult[]> {
  const formData = new FormData();
  formData.append('files', file);
  formData.append('alphaAngle', alphaAngle);
  formData.append('targetStage', stage);
  formData.append('targetPosition', position);

  const authHeaders = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/workorderdetails/${wodKey}/photos/batch-upload`, {
    method: 'POST', headers: { ...authHeaders }, body: formData,
  });
  if (!response.ok) throw new Error(await response.text() || `Upload failed (${response.status})`);

  const reader = response.body?.getReader();
  if (!reader) throw new Error('No response stream');
  const decoder = new TextDecoder();
  let buffer = '';
  let results: BatchUploadResult[] = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split('\n\n');
    buffer = parts.pop() || '';
    for (const part of parts) {
      const dataLine = part.trim().replace(/^data:\s*/, '');
      if (!dataLine) continue;
      try {
        const evt: BatchProgressEvent = JSON.parse(dataLine);
        onProgress?.(evt);
        if (evt.type === 'complete' && evt.results) results = evt.results;
      } catch { /* skip */ }
    }
  }
  return results;
}

export function downloadSinglePhoto(imageUrl: string, fileName: string): void {
  const a = document.createElement('a');
  a.href = imageUrl;
  a.download = fileName;
  a.target = '_blank';
  a.click();
}
